import { jwtDecode } from 'jwt-decode';
import { oktaAuthClient } from '../components/LoginWidget/config';
import { getNativeAuthBridge } from '../utils/native-auth-bridge';
import { getUserId } from './cookies';
import { validateAccessToken, requirePopulatedJWT } from './access-token';
import { shouldUseNativeAuth } from './platform';
import { isValidString } from './common';

const LAST_ACTIVE_CHAT = 'LAST_ACTIVE_CHAT';
const PREVIOUS_VIRUS_SCANNING_PERMISSION = 'PREVIOUS_VIRUS_SCANNING_PERMISSION';
const CHAT_BOX_INPUT_DRAFTS = 'CHAT_BOX_INPUT_DRAFTS';

export function saveLastActiveChat(lastActiveChat) {
  const data = {
    lastActiveChat,
    userId: getUserId(),
  };
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LAST_ACTIVE_CHAT, JSON.stringify(data));
  }
}

export function getLastActiveChat(userId) {
  const str =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(LAST_ACTIVE_CHAT) || ''
      : '';
  try {
    const data = JSON.parse(str);
    if (data.userId === userId) {
      return data.lastActiveChat;
    }
    return '';
  } catch (error) {
    console.error('Error getting last active chat from local storage', error);
    return '';
  }
}

export function savePreviousVirusScanningPermission(
  userId,
  virusScanningPermission,
) {
  const data = {
    virusScanningPermission,
    userId,
  };
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(
      PREVIOUS_VIRUS_SCANNING_PERMISSION,
      JSON.stringify(data),
    );
  }
}

export function getPreviousVirusScanningPermission(userId) {
  const str =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(PREVIOUS_VIRUS_SCANNING_PERMISSION) || ''
      : '';
  try {
    const data = JSON.parse(str);
    if (data?.userId === userId) {
      return data?.virusScanningPermission;
    }
    return null;
  } catch (error) {
    console.error(
      'Error getting previous virus scanning permission from local storage',
      error,
    );
    return null;
  }
}

export function saveChatBoxInputDraft(userId, chatId, text) {
  if (!userId || !chatId || typeof localStorage === 'undefined') {
    return;
  }
  let usersMap = new Map();
  try {
    usersMap =
      // eslint-disable-next-line no-constant-binary-expression
      new Map(JSON?.parse(localStorage?.getItem(CHAT_BOX_INPUT_DRAFTS))) ||
      new Map();
  } catch (error) {
    console.error(
      'Error getting users map chat drafts from local storage',
      error,
    );
  }
  let userDrafts;
  if (!usersMap.has(userId)) {
    usersMap.set(userId, new Map());
    userDrafts = usersMap.get(userId);
  } else {
    try {
      userDrafts = new Map(JSON?.parse(usersMap.get(userId)));
    } catch (error) {
      console.error('Error getting chat map drafts from local storage', error);
    }
  }
  if (text?.trim() === '' && userDrafts?.has(chatId)) {
    userDrafts?.delete(chatId);
  } else if (text?.trim() !== '') {
    userDrafts?.set(chatId, text);
  }
  usersMap?.set(userId, JSON.stringify([...userDrafts]));

  try {
    localStorage.setItem(CHAT_BOX_INPUT_DRAFTS, JSON.stringify([...usersMap]));
  } catch (error) {
    console.error('Error saving chat drafts to local storage', error);
  }
}

export function getChatBoxInputDraft(userId, chatId) {
  if (!userId || !chatId) {
    return '';
  }
  let usersMap = new Map();
  try {
    usersMap =
      // eslint-disable-next-line no-constant-binary-expression
      new Map(JSON?.parse(localStorage?.getItem(CHAT_BOX_INPUT_DRAFTS))) ||
      new Map();
  } catch (error) {
    console.error('Error getting users drafts from local storage', error);
  }
  let thisUserDrafts = usersMap.get(userId);
  try {
    thisUserDrafts = new Map(JSON?.parse(thisUserDrafts));
  } catch (error) {
    console.error('Error getting chat drafts from local storage', error);
  }
  return thisUserDrafts?.get(chatId) || '';
}

export function saveToLocalStorage(key, value) {
  if (typeof key !== 'string') {
    console.log('Cannot save to local storage');
  } else {
    if (typeof localStorage !== 'undefined') {
      const valueType = typeof value;
      localStorage.setItem(
        key,
        valueType === 'object' || valueType === 'array'
          ? JSON.stringify(value)
          : value,
      );
      return true;
    }
    console.log('Cannot save to local storage');
  }
  return false;
}

export function getLocalStorageValue(key) {
  if (typeof key !== 'string') {
    console.log('Cannot get item from local storage');
  } else {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key);
    }
    console.log('Cannot get item from local storage');
  }
  return '';
}

export function cleanLocalStorage() {
  if (typeof localStorage !== 'undefined') {
    localStorage.clear();
  }
}

/**
 * This is intended to be a clone of getOrRenewAccessToken from okta-auth-js
 * but with added logging.
 *
 * Reference: https://github.com/okta/okta-auth-js/blob/master/lib/oidc/mixin/index.ts#L195
 *
 * We are following this document for best token renewal practices:
 * https://support.okta.com/help/s/article/okta-auth-js-token-manager-auto-renew-best-practices?language=en_US
 *
 * Updated to support both native authentication and web authentication
 */

export const getOrRenewAccessToken = async (force = false) => {
  // For native auth, use the bridge directly
  if (shouldUseNativeAuth()) {
    console.log('[Token Manager] Using native auth token retrieval', { force });
    try {
      const nativeBridge = getNativeAuthBridge();
      const result = await nativeBridge.refreshTokens(force);
      const { accessToken } = result;

      if (!accessToken) {
        console.error(
          '[Token Manager] Could not find or renew access token via native auth',
        );
        return null;
      }
      console.log(
        '[Token Manager] Successfully retrieved token via native auth',
      );
      return accessToken;
    } catch (error) {
      console.error(
        '[Token Manager] Native auth token retrieval failed:',
        error,
      );
      return null;
    }
  }

  // For web auth, use the standard oktaAuthClient
  console.log('[Token Manager] Using web auth token retrieval');

  const oktaTokenStorage = JSON.parse(
    localStorage.getItem('okta-token-storage') || '{}',
  );

  console.log(
    '[Token Manager] Local Storage Tokens:',
    JSON.stringify({
      'okta-token-storage': {
        accessToken: {
          hasToken: !!oktaTokenStorage?.accessToken?.accessToken,
          isExpired: oktaTokenStorage?.accessToken?.accessToken
            ? oktaAuthClient.tokenManager.hasExpired(
                oktaTokenStorage?.accessToken,
              )
            : true,
        },
        idToken: {
          hasToken: !!oktaTokenStorage?.idToken?.idToken,
          isExpired: oktaTokenStorage?.idToken?.idToken
            ? oktaAuthClient.tokenManager.hasExpired(oktaTokenStorage?.idToken)
            : true,
        },
        refreshToken: {
          hasToken: !!oktaTokenStorage?.refreshToken?.refreshToken,
          isExpired: oktaTokenStorage?.refreshToken?.refreshToken
            ? oktaAuthClient.tokenManager.hasExpired(
                oktaTokenStorage?.refreshToken,
              )
            : true,
        },
      },
    }),
  );

  const accessToken = await oktaAuthClient.getOrRenewAccessToken();

  if (!accessToken) {
    console.error('[Token Manager] Could not find or renew access token');
    return null;
  }

  if (requirePopulatedJWT()) {
    console.log(
      '[Token Manager] Validating access token inline hook attached properties',
    );
    const isValid = validateAccessToken(accessToken);

    if (!isValid) {
      console.log(
        '[Token Manager] Access token does not have all required properties, force renewing token',
      );
      const renewedAccessToken =
        await oktaAuthClient.tokenManager.renew('accessToken');
      console.log('[Token Manager] Force renewed access token');
      if (!renewedAccessToken && !renewedAccessToken?.accessToken) {
        console.error('[Token Manager] Could not renew access token');
        return null;
      }
      if (!validateAccessToken(renewedAccessToken?.accessToken)) {
        console.error(
          '[Token Manager] Renewal of access token still does not have all required properties',
        );
        return null;
      }
      console.log('[Token Manager] Successfully force renewed access token');
      return renewedAccessToken?.accessToken;
    }
  }

  console.log('[Token Manager] Successfully retrieved token');
  return accessToken;
};

/**
 * This is a wrapper around getOrRenewAccessToken that adds validation and error handling
 * Updated to support both native authentication and web authentication
 */
/**
 * Sentinel thrown when native token acquisition fails (the native bridge
 * couldn't refresh — e.g. dead/rotated refresh token). Unlike a generic error,
 * apiCall treats this as an auth failure and runs its retry-before-deauth path
 * so a terminal native failure signs out instead of stranding the app on a
 * transient error screen.
 */
export const NATIVE_AUTH_FAILURE = 'NATIVE_AUTH_FAILURE';

export const getAccessToken = async (force = false) => {
  try {
    const token = await getOrRenewAccessToken(force);

    if (!isValidString(token)) {
      console.error('[Token Manager] No access token found');
      // In native mode a null token means the bridge could not produce/refresh
      // one -> surface the auth-failure sentinel so apiCall can recover-or-deauth.
      if (shouldUseNativeAuth()) throw NATIVE_AUTH_FAILURE;
      throw new Error('No access token found');
    }

    return token;
  } catch (error) {
    if (error === NATIVE_AUTH_FAILURE) throw error;
    console.error('[Token Manager] Error getting access token:', error);
    if (shouldUseNativeAuth()) throw NATIVE_AUTH_FAILURE;
    throw new Error('Error getting access token');
  }
};

export const shouldRenewAccessToken = () => {
  const tokenStorage = localStorage.getItem('okta-token-storage');
  if (!tokenStorage) return true;

  const oktaTokenStorage = JSON.parse(tokenStorage);
  const accessToken = oktaTokenStorage?.accessToken;
  const accessTokenString = accessToken?.accessToken;
  if (!accessToken) return true;

  const isAccessTokenExpired =
    oktaAuthClient?.tokenManager?.hasExpired(accessToken);
  return (
    shouldUseNativeAuth() ||
    isAccessTokenExpired ||
    (requirePopulatedJWT() && !validateAccessToken(accessTokenString))
  );
};

export const getOktaAccessToken = () => {
  const oktaTokenStorage = JSON.parse(
    localStorage.getItem('okta-token-storage') || '{}',
  );
  const token = oktaTokenStorage?.accessToken?.accessToken;
  if (!isValidString(token)) {
    throw new Error('No access token found');
  }
  return token;
};

export const getOktaIdToken = () => {
  const oktaTokenStorage = JSON.parse(
    localStorage.getItem('okta-token-storage') || '{}',
  );
  return oktaTokenStorage?.idToken;
};

export const getOktaUsername = async () => {
  // For native auth, use the bridge directly
  if (shouldUseNativeAuth()) {
    try {
      const nativeBridge = getNativeAuthBridge();
      const result = await nativeBridge.refreshTokens();
      const { idToken } = result;

      if (!idToken) {
        return null;
      }

      const decodedIdToken = jwtDecode(idToken);
      return decodedIdToken?.preferred_username;
    } catch (error) {
      console.error(
        '[Token Manager] Native auth id token retrieval failed:',
        error,
      );
      return null;
    }
  }
  const oktaTokenStorage = JSON.parse(
    localStorage.getItem('okta-token-storage') || '{}',
  );
  return oktaTokenStorage?.idToken?.claims?.preferred_username;
};
